import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2 } from "lucide-react";
import { useBilling } from "../context/BillingContext";

const CONTENT_TYPES = [
  { k: "social_post", l: "Social post" },
  { k: "linkedin_post", l: "LinkedIn post" },
  { k: "newsletter", l: "Newsletter" },
  { k: "email", l: "Email" },
  { k: "blog_post", l: "Blog post" },
  { k: "presentation_outline", l: "Presentation outline" },
  { k: "kickoff_address", l: "Kickoff address" },
];
const TIMEFRAMES = ["daily", "weekly", "monthly", "quarterly", "yearly"];

export default function ContentStudio() {
  const nav = useNavigate();
  const { handleApiError } = useBilling();
  const [items, setItems] = useState([]);
  const [musings, setMusings] = useState([]);
  const [ctype, setCtype] = useState("linkedin_post");
  const [tf, setTf] = useState("weekly");
  const [extra, setExtra] = useState("");
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/content"), api.get("/musings")]);
    setItems(a.data); setMusings(b.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));

  const generate = async () => {
    setBusy(true);
    try {
      const ids = Object.entries(picked).filter(([,v])=>v).map(([k])=>k);
      const { data } = await api.post("/content/generate", {
        content_type: ctype, timeframe: tf, musing_ids: ids, extra_instructions: extra,
      });
      toast.success("Drafted");
      nav(`/content/${data.id}`);
    } catch (e) {
      if (!handleApiError(e)) toast.error("Generation failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/content/${id}`);
    load();
  };

  return (
    <AppShell eyebrow="AI Studio" title="Content Studio">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-6 lg:col-span-2 space-y-4">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#0033FF]">Compose</div>

          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Content type</label>
            <div className="mt-2 flex flex-wrap gap-1">
              {CONTENT_TYPES.map((t) => (
                <button key={t.k} data-testid={`ctype-${t.k}`} onClick={()=>setCtype(t.k)}
                  className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border ${ctype===t.k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>{t.l}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Timeframe</label>
            <div className="mt-2 flex flex-wrap gap-1">
              {TIMEFRAMES.map((t) => (
                <button key={t} data-testid={`tf-${t}`} onClick={()=>setTf(t)}
                  className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border ${tf===t ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>{t}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Extra instructions (optional)</label>
            <textarea data-testid="extra-instructions" className="w-full mt-2 px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm min-h-[100px]"
              placeholder="e.g. lean into the launch announcement; keep it under 200 words" value={extra} onChange={(e)=>setExtra(e.target.value)} />
          </div>

          <button data-testid="generate-btn" disabled={busy} onClick={generate}
            className="w-full bg-[#0033FF] text-white px-4 py-3 text-xs font-bold tracking-wider uppercase hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Drafting…</> : <><Sparkles className="w-3.5 h-3.5"/> Generate</>}
          </button>
          <p className="text-xs text-zinc-500">Tip: pick specific musings on the right to anchor this piece. Leave empty to use your 10 most recent.</p>
        </div>

        <div className="bg-white p-6 lg:col-span-3">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-4">Pick musings (optional)</div>
          <div className="max-h-[360px] overflow-y-auto divide-y divide-zinc-200 border-t border-zinc-200">
            {musings.length === 0 && <div className="py-6 text-sm text-zinc-500" data-testid="studio-empty-musings">No musings yet. Capture some first.</div>}
            {musings.map((m) => (
              <label key={m.id} data-testid={`pick-musing-${m.id}`} className="flex gap-3 py-3 cursor-pointer hover:bg-zinc-50 px-2 -mx-2">
                <input type="checkbox" checked={!!picked[m.id]} onChange={()=>toggle(m.id)} className="mt-1 accent-[#0033FF]" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0033FF]">{m.musing_type}</span>
                    <span className="text-sm font-semibold truncate">{m.title || "(untitled)"}</span>
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{m.content}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-2xl font-black tracking-tight">Your library</h2>
          <div className="text-xs text-zinc-500">{items.length} pieces</div>
        </div>
        <div className="border border-zinc-200">
          {items.length === 0 && <div className="p-8 text-sm text-zinc-500" data-testid="content-library-empty">Nothing drafted yet.</div>}
          {items.map((c) => (
            <div key={c.id} className="border-b border-zinc-200 last:border-b-0 p-4 flex items-start justify-between gap-4 hover:bg-zinc-50 group" data-testid={`content-item-${c.id}`}>
              <button onClick={()=>nav(`/content/${c.id}`)} data-testid={`open-content-${c.id}`} className="text-left flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0033FF]">{c.content_type.replace(/_/g," ")}</span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{c.timeframe}</span>
                  <span className="text-xs text-zinc-500 font-mono">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-base font-bold">{c.title}</div>
                <div className="text-sm text-zinc-600 line-clamp-2 mt-1">{c.body}</div>
              </button>
              <button onClick={()=>remove(c.id)} data-testid={`delete-content-${c.id}`} className="text-zinc-400 hover:text-[#FF3B30] opacity-0 group-hover:opacity-100">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
